// src/components/stylistdashboard.jsx
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  doc, onSnapshot, collection, query, where,
  orderBy, updateDoc, addDoc, serverTimestamp,
  deleteDoc, limit, writeBatch, getFirestore,
} from 'firebase/firestore';
import { 
  ref as storageRef, deleteObject, uploadBytes, getDownloadURL 
} from 'firebase/storage';
import { updateProfile } from 'firebase/auth';
import { db, auth, storage } from '../firebase';
import { requestNotificationPermission, listenForForegroundMessages } from '../utils/pushConfig';
import Chat from './chat';
import SupportTicket from './SupportTicket';
import toast from 'react-hot-toast';
import { formatNaira } from '../hooks/useSubscription';

// ── CONSTANTS ─────────────────────────────────────────────────────────────────
const NAVY = '#001F3F';
const GOLD = '#B8860B';
const DARK = '#010810';
const SUPER_ADMINS = ["FFXDokpj5IXR3SFSvJyDu70LRp73", "GbjKMdeVEmTgMefcU1cEHsEOstn1"];

const TABS = [
  { key: 'overview',  icon: '☷',  label: 'HOME' },
  { key: 'revenue',   icon: '₦',  label: 'WALLET' },
  { key: 'portfolio', icon: '◈',  label: 'PORTFOLIO' },
  { key: 'settings',  icon: '⚙',  label: 'SETTINGS' },
  { key: 'profile',   icon: '👤', label: 'PROFILE' },
];

// ── RESPONSIVE HOOK ───────────────────────────────────────────────────────────
function useIsMobile() {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  return isMobile;
}

// ── ERROR BOUNDARY ────────────────────────────────────────────────────────────
class ErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { hasError: false }; }
  static getDerivedStateFromError() { return { hasError: true }; }
  render() { return this.state.hasError ? this.props.fallback : this.props.children; }
}

// ==================== IMPROVED UPLOAD STYLE MODAL ====================
function UploadStyleModal({ user, stylist, editItem, onClose }) {
  const [name, setName] = useState(editItem?.name || '');
  const [description, setDescription] = useState(editItem?.description || '');
  const [price, setPrice] = useState(editItem?.price ? String(editItem.price) : '');
  const [category, setCategory] = useState(editItem?.category || 'Hair');
  const [duration, setDuration] = useState(editItem?.duration || '45 min');
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(editItem?.imageUrl || null);
  const [saving, setSaving] = useState(false);
  const [uploadErr, setUploadErr] = useState('');
  const [isCompressing, setIsCompressing] = useState(false);

  const fileRef = useRef(null);
  const objectUrlRef = useRef(null);

  const toastStyle = {
    style: { background: NAVY, color: GOLD, border: `1px solid ${GOLD}`, borderRadius: '12px' },
  };

  const compressImage = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          let { width, height } = img;
          const MAX_DIM = 1200;
          if (width > MAX_DIM || height > MAX_DIM) {
            if (width > height) {
              height = Math.round((height * MAX_DIM) / width);
              width = MAX_DIM;
            } else {
              width = Math.round((width * MAX_DIM) / height);
              height = MAX_DIM;
            }
          }
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);

          canvas.toBlob((blob) => {
            const compressedFile = new File([blob], file.name.replace(/\.\w+$/, '.jpg'), { type: 'image/jpeg' });
            resolve(compressedFile);
          }, 'image/jpeg', 0.82);
        };
        img.src = e.target.result;
      };
      reader.readAsDataURL(file);
    });
  };

  const handleFileSelect = async (e) => {
    const selected = e.target.files?.[0];
    if (!selected) return;

    if (!selected.type.startsWith('image/')) {
      setUploadErr('Please select a valid image file.');
      return;
    }
    if (selected.size > 15 * 1024 * 1024) {
      setUploadErr('Image must be under 15MB.');
      return;
    }

    setIsCompressing(true);
    let fileToUpload = selected;

    try {
      if (selected.size > 5 * 1024 * 1024) {
        const loadingToast = toast.loading('Compressing image...');
        fileToUpload = await compressImage(selected);
        toast.dismiss(loadingToast);
      }

      if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current);
      const previewUrl = URL.createObjectURL(fileToUpload);
      objectUrlRef.current = previewUrl;

      setImageFile(fileToUpload);
      setImagePreview(previewUrl);
      setUploadErr('');
    } catch (err) {
      setUploadErr('Failed to process image.');
    } finally {
      setIsCompressing(false);
    }
  };

  useEffect(() => {
    return () => {
      if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current);
    };
  }, []);

  const handleSave = async () => {
    if (!name.trim()) { setUploadErr('Style name is required.'); return; }
    if (!price || isNaN(Number(price)) || Number(price) <= 0) {
      setUploadErr('Enter a valid price.'); return;
    }
    if (!imagePreview) { setUploadErr('Please upload a style image.'); return; }

    setSaving(true);
    setUploadErr('');
    const toastId = toast.loading('Publishing to Style Vault...', toastStyle);

    try {
      let imageUrl = editItem?.imageUrl || '';

      if (imageFile) {
        if (editItem?.imageUrl) {
          try {
            const oldRef = storageRef(storage, editItem.imageUrl);
            await deleteObject(oldRef);
          } catch {}
        }
        const imgRef = storageRef(storage, `portfolio/${user.uid}/${Date.now()}_${imageFile.name}`);
        await uploadBytes(imgRef, imageFile);
        imageUrl = await getDownloadURL(imgRef);
      }

      const priceNum = Number(price);
      const dbInstance = getFirestore();
      const batch = writeBatch(dbInstance);

      const isEdit = !!editItem?.id;

      const portfolioRef = isEdit 
        ? doc(dbInstance, 'barbers', user.uid, 'portfolio', editItem.id)
        : doc(collection(dbInstance, 'barbers', user.uid, 'portfolio'));

      const galleryRef = doc(collection(dbInstance, 'global_gallery'));
      const vaultRef = doc(collection(dbInstance, 'barbers', user.uid, 'style_vault'));

      const sharedFields = {
        name: name.trim().toUpperCase(),
        description: description.trim(),
        price: priceNum,
        category,
        duration,
        imageUrl,
        barberId: user.uid,
        barberName: user.displayName || stylist?.displayName || 'Professional Stylist',
        businessName: stylist?.businessName || '',
        updatedAt: serverTimestamp(),
      };

      if (isEdit) {
        await updateDoc(portfolioRef, sharedFields);
        if (editItem.galleryId) await updateDoc(doc(dbInstance, 'global_gallery', editItem.galleryId), sharedFields).catch(() => {});
        if (editItem.vaultId) await updateDoc(doc(dbInstance, 'barbers', user.uid, 'style_vault', editItem.vaultId), sharedFields).catch(() => {});
      } else {
        batch.set(portfolioRef, {
          ...sharedFields,
          createdAt: serverTimestamp(),
          galleryId: galleryRef.id,
          vaultId: vaultRef.id,
          promotionStatus: 'approved',
        });

        batch.set(galleryRef, {
          ...sharedFields,
          portfolioId: portfolioRef.id,
          vaultId: vaultRef.id,
          creatorId: user.uid,
          userId: user.uid,
          promotionStatus: 'approved',
          status: 'approved',
          isPromoted: false,
        });

        batch.set(vaultRef, {
          ...sharedFields,
          portfolioId: portfolioRef.id,
          galleryId: galleryRef.id,
          createdAt: serverTimestamp(),
        });

        await batch.commit();
      }

      toast.success(isEdit ? 'Style updated successfully!' : 'Style published to Esdras Style Vault!', { id: toastId, ...toastStyle });
      onClose();

    } catch (err) {
      console.error('[UploadStyleModal]', err);
      setUploadErr('Failed to save. Please try again.');
      toast.error('Upload failed', { id: toastId, ...toastStyle });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={S.overlay}>
      <div style={{ ...S.modal, maxWidth: '520px', maxHeight: '92vh', overflowY: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
          <h3 style={{ margin: 0, color: GOLD, fontSize: '0.95rem', letterSpacing: '2px', fontWeight: '900' }}>
            {editItem ? 'EDIT STYLE' : 'NEW STYLE'}
          </h3>
          <button onClick={onClose} style={S.iconBtn}>✕</button>
        </div>

        <div 
          onClick={() => !saving && fileRef.current?.click()}
          style={{
            width: '100%', aspectRatio: '4/3', borderRadius: '16px', overflow: 'hidden',
            border: imagePreview ? `2px solid ${GOLD}` : '2px dashed rgba(184,134,11,0.4)',
            background: imagePreview ? 'transparent' : 'rgba(255,255,255,0.03)',
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            cursor: (saving || isCompressing) ? 'not-allowed' : 'pointer',
            position: 'relative', marginBottom: '20px'
          }}
        >
          {imagePreview ? (
            <img src={imagePreview} alt="preview" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          ) : (
            <>
              <span style={{ fontSize: '2.8rem', opacity: 0.5 }}>📸</span>
              <p style={{ color: GOLD, fontWeight: '700', margin: '10px 0 4px' }}>Upload Style Photo</p>
              <p style={{ fontSize: '0.65rem', opacity: 0.6 }}>JPG, PNG • Max 15MB</p>
            </>
          )}
          {imagePreview && (
            <div style={{
              position: 'absolute', bottom: '12px', right: '12px',
              background: 'rgba(0,0,0,0.75)', color: GOLD, padding: '5px 12px',
              borderRadius: '20px', fontSize: '0.65rem', fontWeight: '900'
            }}>
              CHANGE PHOTO
            </div>
          )}
          <input ref={fileRef} type="file" hidden accept="image/*" onChange={handleFileSelect} />
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <div>
            <label style={S.inputLabel}>STYLE NAME *</label>
            <input style={S.darkInput} placeholder="e.g. CLEAN LOW FADE WITH BEARD" value={name} onChange={(e) => setName(e.target.value)} disabled={saving} />
          </div>

          <div>
            <label style={S.inputLabel}>PRICE (₦) *</label>
            <input style={S.darkInput} type="number" placeholder="4500" value={price} onChange={(e) => setPrice(e.target.value)} disabled={saving} />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            <div>
              <label style={S.inputLabel}>CATEGORY</label>
              <select style={S.darkInput} value={category} onChange={(e) => setCategory(e.target.value)} disabled={saving}>
                {['Hair', 'Beard', 'Hair + Beard', 'Colour', 'Braids', 'Locs', 'Kids Cut', 'Special'].map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label style={S.inputLabel}>DURATION</label>
              <select style={S.darkInput} value={duration} onChange={(e) => setDuration(e.target.value)} disabled={saving}>
                {['15 min', '30 min', '45 min', '1 hour', '1.5 hours', '2 hours', '2+ hours'].map(d => <option key={d} value={d}>{d}</option>)}
              </select>
            </div>
          </div>

          <div>
            <label style={S.inputLabel}>DESCRIPTION (optional)</label>
            <textarea style={{ ...S.darkInput, height: '90px', resize: 'vertical' }} placeholder="Describe the style, technique, aftercare..." value={description} onChange={(e) => setDescription(e.target.value)} disabled={saving} />
          </div>

          {uploadErr && <div style={{ color: '#ff6b6b', fontSize: '0.8rem', padding: '10px', background: 'rgba(255,107,107,0.1)', borderRadius: '10px' }}>{uploadErr}</div>}

          <button onClick={handleSave} disabled={saving || isCompressing} style={{ ...S.goldBtn, opacity: (saving || isCompressing) ? 0.7 : 1 }}>
            {saving ? 'PUBLISHING...' : isCompressing ? 'COMPRESSING...' : editItem ? 'SAVE CHANGES' : 'PUBLISH TO VAULT'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
//  MAIN STYLIST DASHBOARD COMPONENT
// ─────────────────────────────────────────────────────────────────────────────
export default function StylistDashboard() {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const photoRef = useRef(null);

  // ── STATE ─────────────────────────────────────────────────────────────────
  const [activeTab, setActiveTab] = useState('overview');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [stylist, setStylist] = useState(null);
  const [wallet, setWallet] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [portfolio, setPortfolio] = useState([]);
  const [revenueLogs, setRevenueLogs] = useState([]);
  const [activeChat, setActiveChat] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showPayoutModal, setShowPayoutModal] = useState(false);
  const [showSupport, setShowSupport] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);

  const [optimisticOnline, setOptimisticOnline] = useState(null);
  const [savingBank, setSavingBank] = useState(false);
  const [savingProfile, setSavingProfile] = useState(false);
  const [withdrawing, setWithdrawing] = useState(false);

  const [payoutForm, setPayoutForm] = useState({ amount: '' });
  const [bankSettings, setBankSettings] = useState({
    bankName: '',
    bankCode: '',
    accountNumber: '',
    accountName: ''
  });
  const [profileForm, setProfileForm] = useState({
    displayName: '',
    phone: '',
    businessName: '',
    bio: '',
    location: '',
    specialties: ''
  });

  const user = auth.currentUser;
  const isSuperAdmin = user && SUPER_ADMINS.includes(user.uid);
  const firstName = user?.displayName?.split(' ')[0] || 'Stylist';

  const toastStyle = {
    style: { background: NAVY, color: GOLD, border: `1px solid ${GOLD}`, borderRadius: '12px' },
  };

  // ── EFFECTS ───────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }

    // Notification setup
    (async () => {
      try {
        await requestNotificationPermission(user.uid);
        listenForForegroundMessages();
      } catch (err) {
        console.warn('Push notification setup failed', err);
      }
    })();

    const unsubStylist = onSnapshot(doc(db, 'barbers', user.uid), (snap) => {
      if (snap.exists()) {
        const data = snap.data();
        setStylist(data);
        setOptimisticOnline(data.status === 'active');

        setBankSettings({
          bankName: data.bankName || '',
          bankCode: data.bankCode || '',
          accountNumber: data.accountNumber || '',
          accountName: data.accountName || '',
        });

        setProfileForm({
          displayName: user.displayName || '',
          phone: data.phone || '',
          businessName: data.businessName || '',
          bio: data.bio || '',
          location: data.location || '',
          specialties: (data.specialties || []).join(', '),
        });
      }
    });

    const unsubWallet = onSnapshot(doc(db, 'wallets', user.uid), (snap) => {
      setWallet(snap.exists() ? snap.data() : null);
    });

    const unsubBookings = onSnapshot(
      query(
        collection(db, 'bookings'),
        where('barberId', '==', user.uid),
        where('status', '!=', 'completed'),
        orderBy('createdAt', 'desc'),
        limit(20)
      ),
      (snap) => setBookings(snap.docs.map(d => ({ id: d.id, ...d.data() })))
    );

    const unsubRevenue = onSnapshot(
      query(
        collection(db, 'transactions'),
        where('receiverId', '==', user.uid),
        orderBy('createdAt', 'desc'),
        limit(30)
      ),
      (snap) => setRevenueLogs(snap.docs.map(d => ({ id: d.id, ...d.data() })))
    );

    const unsubPortfolio = onSnapshot(
      query(collection(db, 'barbers', user.uid, 'portfolio'), orderBy('createdAt', 'desc')),
      (snap) => {
        setPortfolio(snap.docs.map(d => ({ id: d.id, ...d.data() })));
        setLoading(false);
      }
    );

    return () => {
      unsubStylist();
      unsubWallet();
      unsubBookings();
      unsubRevenue();
      unsubPortfolio();
    };
  }, [user, navigate]);

  // ── HANDLERS ──────────────────────────────────────────────────────────────
  const toggleStatus = async () => {
    if (!user) return;
    const current = optimisticOnline ?? (stylist?.status === 'active');
    const newStatus = current ? 'inactive' : 'active';

    setOptimisticOnline(!current);

    try {
      await updateDoc(doc(db, 'barbers', user.uid), { status: newStatus });
      toast.success(
        newStatus === 'active' 
          ? '🟢 You are now LIVE — clients can book you' 
          : '🔴 You are OFF-DUTY — hidden from client search',
        toastStyle
      );
    } catch (err) {
      setOptimisticOnline(current);
      toast.error('Failed to update status', toastStyle);
    }
  };

  const saveBankSettings = async () => {
    if (!user) return;
    setSavingBank(true);
    try {
      await updateDoc(doc(db, 'barbers', user.uid), bankSettings);
      toast.success('Bank details saved successfully', toastStyle);
    } catch (err) {
      toast.error('Failed to save bank details', toastStyle);
    } finally {
      setSavingBank(false);
    }
  };

  const handleWithdrawRequest = async () => {
    if (!user || !payoutForm.amount) {
      toast.error('Please enter an amount', toastStyle);
      return;
    }
    setWithdrawing(true);
    try {
      await addDoc(collection(db, 'withdrawals'), {
        userId: user.uid,
        amount: Number(payoutForm.amount),
        status: 'pending',
        createdAt: serverTimestamp(),
        ...bankSettings,
      });
      toast.success('Withdrawal request submitted successfully', toastStyle);
      setShowPayoutModal(false);
      setPayoutForm({ amount: '' });
    } catch (err) {
      toast.error('Failed to submit withdrawal request', toastStyle);
    } finally {
      setWithdrawing(false);
    }
  };

  const deletePortfolioItem = async (id, imageUrl, galleryId, vaultId) => {
    if (!window.confirm('Are you sure you want to delete this style? This action cannot be undone.')) return;

    try {
      const batch = writeBatch(db);
      batch.delete(doc(db, 'barbers', user.uid, 'portfolio', id));
      if (galleryId) batch.delete(doc(db, 'global_gallery', galleryId));
      if (vaultId) batch.delete(doc(db, 'barbers', user.uid, 'style_vault', vaultId));
      await batch.commit();

      if (imageUrl) {
        try {
          const path = imageUrl.split('/o/')[1]?.split('?')[0];
          if (path) await deleteObject(storageRef(storage, decodeURIComponent(path)));
        } catch (e) {}
      }

      toast.success('Style deleted successfully', toastStyle);
    } catch (err) {
      console.error(err);
      toast.error('Failed to delete style', toastStyle);
    }
  };

  const handleProfilePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file || !user) return;

    const toastId = toast.loading('Uploading profile photo...', toastStyle);
    try {
      const sRef = storageRef(storage, `profiles/${user.uid}`);
      await uploadBytes(sRef, file);
      const url = await getDownloadURL(sRef);

      await updateProfile(user, { photoURL: url });
      await updateDoc(doc(db, 'barbers', user.uid), { photoURL: url });
      await updateDoc(doc(db, 'users', user.uid), { photoURL: url });

      toast.success('Profile photo updated!', { id: toastId, ...toastStyle });
    } catch (err) {
      toast.error('Failed to upload photo', { id: toastId, ...toastStyle });
    }
  };

  const saveProfile = async () => {
    if (!user) return;
    setSavingProfile(true);
    try {
      const specialtiesArr = profileForm.specialties.split(',').map(s => s.trim()).filter(Boolean);

      await updateProfile(user, { displayName: profileForm.displayName });
      await updateDoc(doc(db, 'barbers', user.uid), {
        displayName: profileForm.displayName,
        phone: profileForm.phone,
        businessName: profileForm.businessName,
        bio: profileForm.bio,
        location: profileForm.location,
        specialties: specialtiesArr,
        updatedAt: serverTimestamp(),
      });
      await updateDoc(doc(db, 'users', user.uid), {
        displayName: profileForm.displayName,
        phone: profileForm.phone,
        updatedAt: serverTimestamp(),
      });

      toast.success('Profile updated successfully', toastStyle);
    } catch (err) {
      toast.error('Failed to update profile', toastStyle);
    } finally {
      setSavingProfile(false);
    }
  };

  const handleIdentitySwap = async () => {
    if (!isSuperAdmin) return;
    try {
      await updateDoc(doc(db, 'users', user.uid), { role: 'user' });
      toast.success('Switching to Client View...');
      setTimeout(() => {
        navigate('/dashboard');
      }, 1000);
    } catch {
      toast.error('Failed to switch view');
    }
  };

  // ── DERIVED VALUES ────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div style={S.fullCenter}>
        <div style={S.spinner} />
        <h2 style={{ marginTop: '20px', color: GOLD, fontSize: '0.9rem', letterSpacing: '3px' }}>
          SYNCING YOUR HUB...
        </h2>
      </div>
    );
  }

  const isOnline = optimisticOnline ?? (stylist?.status === 'active');
  const walletBal = wallet?.walletBalance || stylist?.walletBalance || 0;
  const hasPendingStyles = portfolio.some(p => p?.promotionStatus === 'pending_approval');

  // ── RENDER ────────────────────────────────────────────────────────────────
  return (
    <ErrorBoundary fallback={<div style={S.fullCenter}><h2 style={{ color: GOLD }}>Error — Please refresh the page</h2></div>}>
      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        @keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.4; } }
        * { box-sizing: border-box; }
        input:focus, textarea:focus, select:focus { outline: none; border-color: ${GOLD} !important; }
      `}</style>

      <div style={{ display: 'flex', minHeight: '100vh', background: DARK, color: 'white', fontFamily: 'Montserrat, sans-serif' }}>

        {/* ==================== DESKTOP SIDEBAR ==================== */}
        {!isMobile && (
          <nav style={S.sidebar}>
            <div style={{ padding: '30px 20px', textAlign: 'center' }}>
              <div style={S.logoText}>ESDRAS</div>
              <p style={{ fontSize: '0.6rem', color: GOLD, letterSpacing: '2px', marginTop: '5px' }}>STYLIST PRO</p>
            </div>
            <div style={S.navItems}>
              {TABS.map(({ key, icon, label }) => (
                <button
                  key={key}
                  onClick={() => setActiveTab(key)}
                  style={activeTab === key ? S.navItemActive : S.navItem}
                >
                  <span style={{ fontSize: '1.1rem' }}>{icon}</span>
                  {label}
                  {key === 'portfolio' && hasPendingStyles && <div style={S.navDot} />}
                </button>
              ))}
            </div>
            <div style={S.sidebarFooter}>
              <button onClick={() => setShowSupport(true)} style={S.supportBtn}>🎧 HELP & SUPPORT</button>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
                <img
                  src={user?.photoURL || `https://ui-avatars.com/api/?name=${firstName}&background=001F3F&color=B8860B`}
                  style={S.miniAvatar}
                  alt="profile"
                />
                <div>
                  <p style={{ margin: 0, fontSize: '0.82rem', fontWeight: 'bold' }}>{firstName}</p>
                  <p style={{ margin: 0, fontSize: '0.6rem', color: GOLD }}>Professional Stylist</p>
                </div>
              </div>
              <button onClick={() => auth.signOut()} style={S.logoutBtn}>LOGOUT</button>
            </div>
          </nav>
        )}

        {/* ==================== MOBILE DRAWER ==================== */}
        {isMobile && (
          <MobileDrawer 
            open={drawerOpen} 
            onClose={() => setDrawerOpen(false)} 
            activeTab={activeTab} 
            setActiveTab={setActiveTab} 
          />
        )}

        {/* ==================== MAIN CONTENT ==================== */}
        <div style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          overflowX: 'hidden',
          paddingBottom: isMobile ? '80px' : '40px',
          background: 'radial-gradient(circle at top right, #021123, #010810)',
        }}>

          {/* TOP BAR */}
          <div style={{ ...S.topBar, padding: isMobile ? '0.9rem 1rem' : '1.2rem 2.5rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              {isMobile && <button onClick={() => setDrawerOpen(true)} style={S.iconBtn}>☰</button>}
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <div style={{
                  width: '8px', height: '8px', borderRadius: '50%',
                  background: isOnline ? '#2ecc71' : '#e74c3c',
                  boxShadow: isOnline ? '0 0 5px #2ecc71' : 'none',
                  animation: isOnline ? 'pulse 2s infinite' : 'none'
                }} />
                <span style={{ fontSize: '0.63rem', fontWeight: '700', color: isOnline ? '#2ecc71' : '#e74c3c' }}>
                  {isOnline ? 'LIVE' : 'OFF-DUTY'}
                </span>
              </div>
            </div>

            <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
              {isSuperAdmin && !isMobile && (
                <button onClick={handleIdentitySwap} style={S.swapBtn}>CLIENT VIEW</button>
              )}
              <button
                onClick={() => { setEditingItem(null); setShowUploadModal(true); }}
                style={S.uploadBtn}
              >
                {isMobile ? '+' : '+ NEW POST'}
              </button>
            </div>
          </div>

          {/* PAGE HEADER */}
          <div style={{ padding: isMobile ? '1.2rem 1rem 0' : '2rem 2.5rem 0' }}>
            <h1 style={{ color: GOLD, fontWeight: '900', margin: '0 0 4px', fontSize: 'clamp(1.1rem, 4vw, 1.6rem)', letterSpacing: '2px' }}>
              {TABS.find(t => t.key === activeTab)?.label || 'OVERVIEW'}
            </h1>
            <p style={{ fontSize: '0.6rem', opacity: 0.35, letterSpacing: '2px', fontWeight: 'bold', margin: 0 }}>
              ESDRAS STYLIST PRO • {stylist?.referralCode || 'MEMBER'}
            </p>
          </div>

          {/* TAB CONTENT AREA */}
          <div style={{ padding: isMobile ? '1.2rem 1rem' : '1.5rem 2.5rem', flex: 1 }}>

            {/* ==================== OVERVIEW TAB ==================== */}
            {activeTab === 'overview' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                <div style={S.welcomeCard}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
                    <div style={{ position: 'relative', cursor: 'pointer' }} onClick={() => photoRef.current?.click()}>
                      <img
                        src={user?.photoURL || `https://ui-avatars.com/api/?name=${firstName}&background=001F3F&color=B8860B`}
                        style={S.avatarLg}
                        alt="profile"
                      />
                      <div style={S.cameraOverlay}>📸</div>
                      <input type="file" ref={photoRef} hidden accept="image/*" onChange={handleProfilePhotoUpload} />
                    </div>
                    <div>
                      <p style={{ margin: 0, fontSize: '0.6rem', color: GOLD, letterSpacing: '3px', fontWeight: '900' }}>WELCOME BACK</p>
                      <h2 style={{ margin: '4px 0', fontSize: 'clamp(1.2rem, 4vw, 1.8rem)', fontWeight: '900', color: 'white' }}>
                        {firstName}
                      </h2>
                      {stylist?.businessName && <p style={{ margin: 0, fontSize: '0.7rem', color: 'rgba(255,255,255,0.5)' }}>{stylist.businessName}</p>}
                    </div>
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginTop: '18px', flexWrap: 'wrap' }}>
                    <button
                      onClick={toggleStatus}
                      style={{
                        display: 'flex', alignItems: 'center', gap: '10px',
                        background: isOnline ? 'rgba(46,204,113,0.12)' : 'rgba(231,76,60,0.12)',
                        padding: '10px 20px', borderRadius: '50px',
                        border: `1.5px solid ${isOnline ? '#2ecc71' : '#e74c3c'}`,
                        cursor: 'pointer'
                      }}
                    >
                      <div style={{
                        width: '9px', height: '9px', borderRadius: '50%',
                        background: isOnline ? '#2ecc71' : '#e74c3c',
                        boxShadow: isOnline ? '0 0 6px #2ecc71' : 'none'
                      }} />
                      <span style={{ fontSize: '0.68rem', fontWeight: '900', color: isOnline ? '#2ecc71' : '#e74c3c' }}>
                        {isOnline ? 'LIVE — TAP TO GO OFF-DUTY' : 'OFF-DUTY — TAP TO GO LIVE'}
                      </span>
                    </button>
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : '1fr 1fr 1fr', gap: '16px' }}>
                  {[
                    { label: 'WALLET BALANCE', value: formatNaira(walletBal), accent: true },
                    { label: 'ACTIVE BOOKINGS', value: String(bookings.length) },
                    { label: 'STYLES POSTED', value: String(portfolio.length) },
                  ].map((s, i) => (
                    <div key={i} style={S.statCard}>
                      <p style={S.statLabel}>{s.label}</p>
                      <p style={{ ...S.statValue, color: s.accent ? GOLD : 'white' }}>{s.value}</p>
                    </div>
                  ))}
                </div>

                <button onClick={() => setShowPayoutModal(true)} style={S.goldBtn}>WITHDRAW FUNDS</button>
              </div>
            )}

            {/* ==================== REVENUE TAB ==================== */}
            {activeTab === 'revenue' && (
              <div style={S.section}>
                <p style={S.sectionTitle}>WALLET TRANSACTIONS</p>
                {revenueLogs.length === 0 ? (
                  <p style={S.emptyText}>No recent transactions yet.</p>
                ) : (
                  revenueLogs.map(log => (
                    <div key={log.id} style={S.logRow}>
                      <div>
                        <p style={{ fontWeight: '700', margin: 0, fontSize: '0.82rem' }}>
                          {(log.type || '').replace(/_/g, ' ')}
                        </p>
                        <p style={{ fontSize: '0.6rem', opacity: 0.4, margin: '2px 0 0' }}>
                          {log.createdAt ? log.createdAt.toDate().toLocaleDateString('en-NG', { day: 'numeric', month: 'short', year: 'numeric' }) : ''}
                        </p>
                      </div>
                      <p style={{ fontWeight: '900', color: GOLD, margin: 0 }}>{formatNaira(log.amount)}</p>
                    </div>
                  ))
                )}
              </div>
            )}

            {/* ==================== PORTFOLIO TAB ==================== */}
            {activeTab === 'portfolio' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <p style={{ ...S.sectionTitle, margin: 0 }}>YOUR MASTERPIECES</p>
                  <button
                    onClick={() => { setEditingItem(null); setShowUploadModal(true); }}
                    style={{ ...S.goldBtn, width: 'auto', padding: '12px 24px' }}
                  >
                    + NEW POST
                  </button>
                </div>

                {portfolio.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '60px 20px', opacity: 0.6 }}>
                    <p style={{ fontSize: '3rem', marginBottom: '16px' }}>◈</p>
                    <p>No styles posted yet.</p>
                    <p style={{ fontSize: '0.8rem', opacity: 0.6 }}>Upload your first style to appear in the Global Gallery and Style Vault.</p>
                  </div>
                ) : (
                  <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(auto-fill, minmax(220px, 1fr))', gap: '16px' }}>
                    {portfolio.map(item => (
                      <div key={item.id} style={S.portfolioCard}>
                        <img src={item.imageUrl} alt={item.name} style={S.portfolioImg} onError={(e) => e.target.src = 'https://placehold.co/600x400/000000/B8860B?text=Esdras+Style'} />
                        <div style={{ padding: '12px' }}>
                          <p style={{ margin: 0, fontWeight: '700', fontSize: '0.85rem' }}>{item.name}</p>
                          <p style={{ margin: '4px 0', color: GOLD, fontWeight: 'bold' }}>{formatNaira(item.price)}</p>
                          {item.galleryId && <span style={{ fontSize: '0.55rem', color: '#2ecc71' }}>✓ In Global Gallery</span>}
                        </div>
                        <div style={{ display: 'flex', gap: '8px', padding: '0 12px 12px' }}>
                          <button onClick={() => { setEditingItem(item); setShowUploadModal(true); }} style={S.miniBtn}>✏️ Edit</button>
                          <button onClick={() => deletePortfolioItem(item.id, item.imageUrl, item.galleryId, item.vaultId)} style={{ ...S.miniBtn, color: '#ff4d4d' }}>🗑️</button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* ==================== SETTINGS TAB ==================== */}
            {activeTab === 'settings' && (
              <div style={S.section}>
                <p style={S.sectionTitle}>PAYOUT SETTINGS</p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                  {[
                    { key: 'bankName', label: 'Bank Name', placeholder: 'e.g. Access Bank' },
                    { key: 'bankCode', label: 'Bank Code', placeholder: 'e.g. 044' },
                    { key: 'accountNumber', label: 'Account Number', placeholder: '10-digit NUBAN' },
                    { key: 'accountName', label: 'Account Name', placeholder: 'Name on account' },
                  ].map(f => (
                    <div key={f.key}>
                      <label style={S.inputLabel}>{f.label}</label>
                      <input
                        style={S.darkInput}
                        placeholder={f.placeholder}
                        value={bankSettings[f.key]}
                        onChange={e => setBankSettings({ ...bankSettings, [f.key]: e.target.value })}
                      />
                    </div>
                  ))}
                  <button onClick={saveBankSettings} style={S.goldBtn} disabled={savingBank}>
                    {savingBank ? 'SAVING...' : 'SAVE BANK DETAILS'}
                  </button>
                </div>
              </div>
            )}

            {/* ==================== PROFILE TAB ==================== */}
            {activeTab === 'profile' && (
              <div style={S.section}>
                <p style={S.sectionTitle}>EDIT PROFILE</p>
                <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '24px' }}>
                  <div style={{ position: 'relative', cursor: 'pointer' }} onClick={() => photoRef.current?.click()}>
                    <img
                      src={user?.photoURL || `https://ui-avatars.com/api/?name=${firstName}&background=001F3F&color=B8860B`}
                      style={S.avatarXl}
                      alt="profile"
                    />
                    <div style={S.cameraOverlayLg}>📸 CHANGE</div>
                    <input type="file" ref={photoRef} hidden accept="image/*" onChange={handleProfilePhotoUpload} />
                  </div>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                  {[
                    { key: 'displayName', label: 'Full Name', placeholder: 'Your full name' },
                    { key: 'phone', label: 'Phone Number', placeholder: '+234 xxx xxx xxxx' },
                    { key: 'businessName', label: 'Business / Studio Name', placeholder: 'Your brand name' },
                    { key: 'location', label: 'Location', placeholder: 'e.g. Lekki, Lagos' },
                    { key: 'specialties', label: 'Specialties', placeholder: 'Fades, Braids, Locs (comma separated)' },
                  ].map(f => (
                    <div key={f.key}>
                      <label style={S.inputLabel}>{f.label}</label>
                      <input
                        style={S.darkInput}
                        placeholder={f.placeholder}
                        value={profileForm[f.key]}
                        onChange={e => setProfileForm({ ...profileForm, [f.key]: e.target.value })}
                      />
                    </div>
                  ))}

                  <div>
                    <label style={S.inputLabel}>Bio</label>
                    <textarea
                      style={{ ...S.darkInput, height: '100px', resize: 'vertical' }}
                      placeholder="Tell clients about your style, experience..."
                      value={profileForm.bio}
                      onChange={e => setProfileForm({ ...profileForm, bio: e.target.value })}
                    />
                  </div>

                  <button onClick={saveProfile} style={S.goldBtn} disabled={savingProfile}>
                    {savingProfile ? 'SAVING...' : 'SAVE PROFILE'}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* BOTTOM NAV FOR MOBILE */}
        {isMobile && (
          <BottomNav activeTab={activeTab} setActiveTab={setActiveTab} hasPendingStyles={hasPendingStyles} />
        )}

        {/* MODALS */}
        {showUploadModal && (
          <UploadStyleModal
            user={user}
            stylist={stylist}
            editItem={editingItem}
            onClose={() => {
              setShowUploadModal(false);
              setEditingItem(null);
            }}
          />
        )}

        {/* PAYOUT MODAL */}
        {showPayoutModal && (
          <div style={S.overlay}>
            <div style={{ ...S.modal, maxWidth: '420px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <h3 style={{ margin: 0, color: GOLD, fontSize: '1.1rem' }}>WITHDRAW FUNDS</h3>
                <button onClick={() => setShowPayoutModal(false)} style={S.iconBtn}>✕</button>
              </div>

              <p style={{ fontSize: '0.85rem', marginBottom: '20px' }}>
                Available Balance: <strong style={{ color: GOLD }}>{formatNaira(walletBal)}</strong>
              </p>

              <div style={{ marginBottom: '16px' }}>
                <label style={S.inputLabel}>AMOUNT (₦)</label>
                <input
                  style={S.darkInput}
                  type="number"
                  placeholder="Enter amount"
                  value={payoutForm.amount}
                  onChange={(e) => setPayoutForm({ ...payoutForm, amount: e.target.value })}
                />
              </div>

              <div style={{ fontSize: '0.75rem', opacity: 0.7, marginBottom: '20px' }}>
                 Bank: {bankSettings.bankName || 'Not set'}<br />
                 Account: {bankSettings.accountNumber || 'Not set'}
             </div>

             <div style={{ display: 'flex', gap: '12px' }}>
               <button 
                 onClick={handleWithdrawRequest} 
                 disabled={withdrawing || !payoutForm.amount}
                 style={{ ...S.goldBtn, flex: 1 }}
               >
                 {withdrawing ? 'PROCESSING...' : 'CONFIRM WITHDRAWAL'}
               </button>
               <button 
                 onClick={() => setShowPayoutModal(false)}
                 style={{ 
                   flex: 1, background: 'none', border: '1px solid rgba(255,255,255,0.2)', 
                   color: 'white', padding: '14px', borderRadius: '14px', cursor: 'pointer' 
                 }}
               >
                 CANCEL
               </button>
             </div>

             <p style={{ fontSize: '0.65rem', opacity: 0.5, textAlign: 'center', marginTop: '16px' }}>
               Withdrawals are processed within 24-48 hours
             </p>
           </div>
         </div>
       )}

        {showSupport && <SupportTicket onClose={() => setShowSupport(false)} />}
        {activeChat && <Chat chatId={activeChat.id} stylistId={user?.uid} onClose={() => setActiveChat(null)} />}
      </div>
    </ErrorBoundary>
  );
}

// ── HELPER COMPONENTS ─────────────────────────────────────────────────────
function BottomNav({ activeTab, setActiveTab, hasPendingStyles }) {
  return (
    <div style={S.bottomNav}>
      {TABS.map(({ key, icon, label }) => (
        <button key={key} onClick={() => setActiveTab(key)} style={S.bottomNavBtn(activeTab === key)}>
          <span style={{ fontSize: '1.1rem', position: 'relative' }}>
            {icon}
            {key === 'portfolio' && hasPendingStyles && <span style={S.bottomNavDot} />}
          </span>
          <span style={{ fontSize: '0.42rem', letterSpacing: '1px', fontWeight: '900' }}>{label}</span>
        </button>
      ))}
    </div>
  );
}

function MobileDrawer({ open, onClose, activeTab, setActiveTab }) {
  return (
    <>
      {open && <div onClick={onClose} style={S.drawerOverlay} />}
      <div style={{ 
        ...S.drawer, 
        transform: open ? 'translateX(0)' : 'translateX(-100%)' 
      }}>
        <div style={S.drawerHeader}>
          <div>
            <div style={S.logoText}>ESDRAS</div>
            <p style={{ fontSize: '0.58rem', color: GOLD, margin: 0 }}>STYLIST PRO</p>
          </div>
          <button onClick={onClose} style={S.iconBtn}>✕</button>
        </div>

        <div style={{ flex: 1, padding: '20px 15px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
          {TABS.map(({ key, icon, label }) => (
            <button
              key={key}
              onClick={() => { setActiveTab(key); onClose(); }}
              style={activeTab === key ? S.drawerItemActive : S.drawerItem}
            >
              {icon} {label}
            </button>
          ))}
        </div>

        <div style={{ padding: '20px', borderTop: '1px solid #111' }}>
          <button 
            onClick={() => auth.signOut()} 
            style={S.logoutBtn}
          >
            LOGOUT
          </button>
        </div>
      </div>
    </>
  );
}

// ── STYLES ───────────────────────────────────────────────────────────────────
const S = {
  fullCenter: { height: '100vh', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', background: DARK, color: GOLD, fontFamily: 'Montserrat, sans-serif' },
  spinner: { width: '44px', height: '44px', border: '3px solid rgba(184,134,11,0.15)', borderTop: `3px solid ${GOLD}`, borderRadius: '50%', animation: 'spin 1s linear infinite' },

  sidebar: { width: '260px', background: '#000', borderRight: '1px solid #111', display: 'flex', flexDirection: 'column', height: '100vh', position: 'sticky', top: 0, zIndex: 1000, flexShrink: 0 },
  logoText: { color: GOLD, fontSize: '1.8rem', fontWeight: '900', letterSpacing: '6px' },
  navItems: { flex: 1, display: 'flex', flexDirection: 'column', gap: '4px', padding: '10px 15px' },
  navItem: { background: 'none', border: 'none', color: 'rgba(255,255,255,0.45)', padding: '13px 16px', borderRadius: '12px', cursor: 'pointer', fontSize: '0.75rem', fontWeight: '700', display: 'flex', alignItems: 'center', gap: '12px', fontFamily: 'Montserrat, sans-serif' },
  navItemActive: { background: 'rgba(184,134,11,0.12)', color: GOLD, padding: '13px 16px', borderRadius: '12px', cursor: 'pointer', fontSize: '0.75rem', fontWeight: '700', display: 'flex', alignItems: 'center', gap: '12px', fontFamily: 'Montserrat, sans-serif' },
  navDot: { width: '7px', height: '7px', borderRadius: '50%', background: '#ffae00', marginLeft: 'auto' },
  sidebarFooter: { padding: '20px', borderTop: '1px solid #111' },

  supportBtn: { width: '100%', background: 'rgba(184,134,11,0.05)', border: `1px solid rgba(184,134,11,0.3)`, color: GOLD, padding: '12px', borderRadius: '10px', fontSize: '0.7rem', fontWeight: 'bold', cursor: 'pointer', marginBottom: '16px' },
  logoutBtn: { width: '100%', background: 'rgba(231,76,60,0.1)', color: '#e74c3c', border: '1px solid rgba(231,76,60,0.2)', padding: '12px', borderRadius: '10px', fontWeight: 'bold', cursor: 'pointer' },
  miniAvatar: { width: '40px', height: '40px', borderRadius: '50%', border: `2px solid ${GOLD}`, objectFit: 'cover' },

  topBar: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(15px)', position: 'sticky', top: 0, zIndex: 900 },
  iconBtn: { background: 'none', border: 'none', color: 'white', fontSize: '1.3rem', cursor: 'pointer' },
  uploadBtn: { background: GOLD, color: 'black', border: 'none', padding: '10px 18px', borderRadius: '10px', fontSize: '0.78rem', fontWeight: '900', cursor: 'pointer' },
  swapBtn: { background: 'rgba(255,255,255,0.1)', color: 'white', border: '1px solid rgba(255,255,255,0.2)', padding: '8px 16px', borderRadius: '10px', fontSize: '0.7rem' },

  welcomeCard: { background: 'rgba(255,255,255,0.03)', border: `1px solid rgba(184,134,11,0.15)`, borderRadius: '24px', padding: '24px' },
  statCard: { background: 'rgba(255,255,255,0.02)', padding: '20px', borderRadius: '20px', border: '1px solid rgba(255,255,255,0.05)' },
  statLabel: { opacity: 0.4, fontSize: '0.58rem', fontWeight: 'bold', letterSpacing: '1px', margin: '0 0 8px' },
  statValue: { fontWeight: '900', margin: 0, fontSize: '1.1rem' },

  section: { background: 'rgba(255,255,255,0.02)', padding: '20px', borderRadius: '22px', border: '1px solid rgba(184,134,11,0.08)' },
  sectionTitle: { fontSize: '0.72rem', color: GOLD, letterSpacing: '2px', fontWeight: '900', margin: '0 0 16px' },
  emptyText: { opacity: 0.4, fontSize: '0.82rem', fontStyle: 'italic', margin: 0 },

  logRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 0', borderBottom: '1px solid rgba(255,255,255,0.05)' },
  miniBtn: { background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', color: 'white', padding: '8px 12px', borderRadius: '10px', cursor: 'pointer', fontSize: '0.75rem' },

  portfolioCard: { background: '#111', borderRadius: '16px', overflow: 'hidden', border: '1px solid rgba(255,255,255,0.05)' },
  portfolioImg: { width: '100%', height: '160px', objectFit: 'cover', display: 'block' },

  avatarLg: { width: '72px', height: '72px', borderRadius: '50%', border: `3px solid ${GOLD}`, objectFit: 'cover' },
  avatarXl: { width: '100px', height: '100px', borderRadius: '50%', border: `3px solid ${GOLD}`, objectFit: 'cover' },
  cameraOverlay: { position: 'absolute', bottom: '2px', right: '2px', background: GOLD, borderRadius: '50%', width: '22px', height: '22px', display: 'grid', placeItems: 'center', fontSize: '0.6rem', color: 'black' },
  cameraOverlayLg: { position: 'absolute', bottom: '6px', left: '50%', transform: 'translateX(-50%)', background: 'rgba(0,0,0,0.75)', color: GOLD, fontSize: '0.55rem', fontWeight: '900', padding: '3px 8px', borderRadius: '6px' },

  inputLabel: { fontSize: '0.6rem', color: GOLD, fontWeight: 'bold', letterSpacing: '1px', display: 'block', marginBottom: '6px' },
  darkInput: { background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)', color: 'white', padding: '13px 14px', borderRadius: '12px', width: '100%', fontFamily: 'Montserrat, sans-serif', fontSize: '0.82rem' },
  goldBtn: { background: GOLD, color: 'black', border: 'none', padding: '14px 20px', borderRadius: '14px', fontWeight: '900', width: '100%', cursor: 'pointer', fontFamily: 'Montserrat, sans-serif', fontSize: '0.78rem', letterSpacing: '1px' },

  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.92)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000, padding: '20px' },
  modal: { background: '#010810', padding: '28px', borderRadius: '28px', border: `1px solid rgba(184,134,11,0.4)`, width: '100%' },

  bottomNav: { position: 'fixed', bottom: 0, left: 0, right: 0, background: 'rgba(0,0,0,0.95)', borderTop: '1px solid #1a1a1a', display: 'flex', zIndex: 1000, backdropFilter: 'blur(20px)' },
  bottomNavBtn: (active) => ({ 
    flex: 1, background: 'none', border: 'none', 
    color: active ? GOLD : 'rgba(255,255,255,0.35)', 
    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px', 
    padding: '10px 0 8px', cursor: 'pointer' 
  }),
  bottomNavDot: { position: 'absolute', top: '-3px', right: '-4px', width: '7px', height: '7px', borderRadius: '50%', background: '#ffae00' },

  drawerOverlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', zIndex: 1400 },
  drawer: { position: 'fixed', top: 0, left: 0, bottom: 0, width: '280px', background: '#000', borderRight: '1px solid #1a1a1a', zIndex: 1500, transition: 'transform 0.28s ease' },
  drawerHeader: { padding: '28px 20px', borderBottom: '1px solid #111', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  drawerItem: { background: 'none', border: 'none', color: 'rgba(255,255,255,0.5)', padding: '14px', borderRadius: '12px', cursor: 'pointer', fontSize: '0.8rem', fontWeight: '700', textAlign: 'left', width: '100%', fontFamily: 'Montserrat, sans-serif' },
  drawerItemActive: { background: 'rgba(184,134,11,0.12)', color: GOLD, padding: '14px', borderRadius: '12px', cursor: 'pointer', fontSize: '0.8rem', fontWeight: '700', textAlign: 'left', width: '100%', fontFamily: 'Montserrat, sans-serif' },
};
